import React, { useState, useEffect, useMemo } from "react";
import { api, fetchBlob } from "../api/client";
import { BarChart3, AlertTriangle, Download } from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";

const AGING_COLS = [
    { key: "d0", label: "Today" },
    { key: "d1", label: "1 day" },
    { key: "d2_7", label: "2-7 days" },
    { key: "d8_30", label: "8-30 days" },
    { key: "d30plus", label: "30+ days" },
];

const SEV_COLOR = {
    critical: "#dc2626", high: "#ea580c", warning: "#2563eb",
    medium: "#2563eb", low: "#64748b", info: "#64748b",
};

function num(n) {
    return n > 0 ? <strong>{n}</strong> : <span style={{ color: "var(--text-muted, #cbd5e1)" }}>0</span>;
}
function yday(n) {
    return n > 0 ? <strong style={{ color: "#2563eb" }}>{n}</strong> : <span style={{ color: "var(--text-muted, #cbd5e1)" }}>0</span>;
}
function StatusPill({ status }) {
    const s = (status || "").toLowerCase();
    const fail = s !== "resolved";
    const label = s === "resolved" ? "Resolved" : s === "reopened" ? "REOPENED" : "STILL OPEN";
    return (
        <span style={{
            fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 10, whiteSpace: "nowrap",
            color: fail ? "#dc2626" : "#16a34a", background: fail ? "#fee2e2" : "#dcfce7",
        }}>{label}</span>
    );
}

export default function OpsOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRpl, setSelectedRpl] = useState(""); // "" = manager overview
    const [downloading, setDownloading] = useState(false);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const blob = await fetchBlob(`/reports/ops-overview/export.xlsx`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `OpsAgingReport_${data?.window?.today || "today"}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError("Download failed: " + err.message);
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        api.reportsOpsOverview()
            .then(d => { setData(d); setError(null); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    const totals = useMemo(() => {
        const t = { d0: 0, d1: 0, d2_7: 0, d8_30: 0, d30plus: 0, total: 0, yday: 0 };
        for (const r of (data?.rpls || [])) {
            t.d0 += r.aging.d0; t.d1 += r.aging.d1; t.d2_7 += r.aging.d2_7;
            t.d8_30 += r.aging.d8_30; t.d30plus += r.aging.d30plus; t.total += r.aging.total;
            t.yday += r.openedYesterday;
        }
        return t;
    }, [data]);

    const th = { padding: "7px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3, color: "var(--text-secondary)", borderBottom: "2px solid var(--border)", textAlign: "center", whiteSpace: "nowrap" };
    const thL = { ...th, textAlign: "left" };
    const td = { padding: "7px 10px", fontSize: 13, borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))", textAlign: "center" };
    const tdL = { ...td, textAlign: "left", fontWeight: 600 };

    const AgingHead = ({ first }) => (
        <tr>
            <th style={thL}>{first}</th>
            {AGING_COLS.map(c => <th key={c.key} style={th}>{c.label}</th>)}
            <th style={th}>Open</th>
            <th style={th}>Opened y'day</th>
        </tr>
    );
    const AgingRow = ({ label, aging, opened, onClick }) => (
        <tr>
            <td style={{ ...tdL, ...(onClick ? { cursor: "pointer", color: "var(--primary)", textDecoration: "underline" } : {}) }} onClick={onClick}>{label}</td>
            {AGING_COLS.map(c => <td key={c.key} style={td}>{num(aging[c.key])}</td>)}
            <td style={{ ...td, fontWeight: 700 }}>{aging.total || 0}</td>
            <td style={td}>{yday(opened)}</td>
        </tr>
    );

    if (loading) return <LoadingSpinner />;
    if (error) return <div style={{ padding: 24, color: "var(--red)" }}><AlertTriangle size={16} /> {error}</div>;

    const rpls = data?.rpls || [];
    const activeRpl = selectedRpl ? rpls.find(r => r.rplName === selectedRpl) : null;

    return (
        <div>
            <div className="page-header">
                <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <BarChart3 size={22} /> Ops Overview
                </h2>
                <p style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                    Same data as the daily digest emails. Aging columns are still-open issues by age;
                    "Opened y'day" counts issues opened in the covered period ({data?.dateLabel}).
                </p>
            </div>

            {/* Filter: manager overview or a single RPL */}
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
                <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>View</label>
                <select className="filter-select" value={selectedRpl} onChange={e => setSelectedRpl(e.target.value)} style={{ fontSize: 13, minWidth: 240 }}>
                    <option value="">Manager overview{data?.stateManagerName ? ` — ${data.stateManagerName}` : ""}</option>
                    {rpls.map(r => <option key={r.rplName} value={r.rplName}>{r.rplName} ({r.aging.total} open)</option>)}
                </select>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: "auto" }}>
                    {totals.total} open &middot; {totals.yday} opened y'day &middot; {rpls.length} RPLs
                </span>
                <button onClick={onDownload} disabled={downloading}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", fontSize: 13,
                        fontWeight: 600, borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)",
                        color: "var(--text-secondary)", cursor: downloading ? "wait" : "pointer", opacity: downloading ? 0.7 : 1,
                    }}>
                    <Download size={14} /> {downloading ? "Preparing..." : "Excel aging report"}
                </button>
            </div>

            {!activeRpl && (
                <>
                    {/* Table 1 — RPL rollup */}
                    <h3 style={{ fontSize: 15, margin: "8px 0" }}>By RPL</h3>
                    <div style={{ overflowX: "auto", marginBottom: 28 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                            <thead><AgingHead first="RPL" /></thead>
                            <tbody>
                                {rpls.map(r => (
                                    <AgingRow key={r.rplName} label={r.rplName} aging={r.aging} opened={r.openedYesterday}
                                        onClick={() => setSelectedRpl(r.rplName)} />
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: "var(--bg-hover, rgba(0,0,0,0.03))" }}>
                                    <td style={{ ...tdL, fontWeight: 700 }}>TOTAL</td>
                                    {AGING_COLS.map(c => <td key={c.key} style={{ ...td, fontWeight: 700 }}>{totals[c.key]}</td>)}
                                    <td style={{ ...td, fontWeight: 800 }}>{totals.total}</td>
                                    <td style={{ ...td, fontWeight: 800, color: "#2563eb" }}>{totals.yday}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Table 2 — sites grouped by RPL */}
                    <h3 style={{ fontSize: 15, margin: "8px 0" }}>Sites by RPL</h3>
                    {rpls.map(r => (
                        <div key={r.rplName} style={{ marginBottom: 18 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, margin: "10px 0 4px" }}>
                                {r.rplName} <span style={{ color: "var(--text-secondary)", fontWeight: 500 }}>&middot; {r.aging.total} open &middot; {r.openedYesterday} opened y'day</span>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                                    <thead><AgingHead first="Site" /></thead>
                                    <tbody>
                                        {r.sites.map(s => (
                                            <AgingRow key={s.siteId} label={`${s.siteId} ${s.storeName || ""}`} aging={s.aging} opened={s.openedYesterday} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </>
            )}

            {activeRpl && (
                <>
                    {/* Single RPL: site aging statement */}
                    <h3 style={{ fontSize: 15, margin: "8px 0" }}>{activeRpl.rplName} — outstanding by site</h3>
                    <div style={{ overflowX: "auto", marginBottom: 28 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                            <thead><AgingHead first="Site" /></thead>
                            <tbody>
                                {activeRpl.sites.map(s => (
                                    <AgingRow key={s.siteId} label={`${s.siteId} ${s.storeName || ""}`} aging={s.aging} opened={s.openedYesterday} />
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ background: "var(--bg-hover, rgba(0,0,0,0.03))" }}>
                                    <td style={{ ...tdL, fontWeight: 700 }}>TOTAL</td>
                                    {AGING_COLS.map(c => <td key={c.key} style={{ ...td, fontWeight: 700 }}>{activeRpl.aging[c.key]}</td>)}
                                    <td style={{ ...td, fontWeight: 800 }}>{activeRpl.aging.total}</td>
                                    <td style={{ ...td, fontWeight: 800, color: "#2563eb" }}>{activeRpl.openedYesterday}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Single RPL: issues opened in the window (the "invoice") */}
                    <h3 style={{ fontSize: 15, margin: "8px 0" }}>
                        Issues opened {data?.dateLabel} ({activeRpl.openedYesterday})
                    </h3>
                    {activeRpl.sites.filter(s => s.newIssues.length > 0).length === 0 && (
                        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>No new issues opened in this period.</p>
                    )}
                    {activeRpl.sites.filter(s => s.newIssues.length > 0).map(s => (
                        <div key={s.siteId} style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 700, fontSize: 13, margin: "8px 0 2px" }}>{s.siteId} {s.storeName || ""}</div>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <tbody>
                                    {s.newIssues.map((i, idx) => (
                                        <tr key={idx}>
                                            <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))", verticalAlign: "top", width: 8 }}>
                                                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 999, background: SEV_COLOR[(i.severity || "").toLowerCase()] || "#64748b" }} />
                                            </td>
                                            <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))", verticalAlign: "top" }}>
                                                <div><strong>{i.fixture || i.title}</strong>{i.fixture && i.title && i.fixture !== i.title ? ` — ${i.title}` : ""}</div>
                                                {i.comment && <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>{i.comment}</div>}
                                            </td>
                                            <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-light, rgba(255,255,255,0.05))", textAlign: "right", verticalAlign: "top" }}>
                                                <StatusPill status={i.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}
